/**
 * Lead Command Center — Dashboard (awareness only).
 *
 * Five simple cards that mirror LeadLoop's numbers, three ways in (LeadLoop,
 * New Manual Lead, Form Submissions), and Recent Activity. All the actual
 * work — approving, recording interactions, setting next steps — happens in
 * LeadLoop and Lead Detail, never here.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ClipboardCheck, History, Inbox, Plus, Repeat } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { lccApi } from '@/lib/lcc-api';
import {
  formatLccDateTime,
  formatLccLabel,
  LccErrorCard,
  LccLoadingCard,
} from './lcc/lcc-shared';
import { CreateLeadDialog } from './lcc/lead-form-dialog';

export function LeadCommandCenterView() {
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['lcc', 'dashboard'],
    queryFn: () => lccApi.dashboard(),
    refetchInterval: 30_000,
  });

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5">
      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="flex items-center gap-2 text-[22px] font-semibold leading-tight">
            <ClipboardCheck className="h-5 w-5 text-primary" aria-hidden />
            Lead Command Center
          </h1>
          <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
            Demo · Safe mode
          </Badge>
        </div>
        <p className="max-w-[70ch] text-[13px] leading-snug text-muted-foreground">
          A quick read on where things stand. The work happens in LeadLoop — open it, work the
          leads that are due, record what happened, set the next step.
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        <Button asChild size="sm" className="gap-1.5">
          <Link to="/demo/lcc/loop">
            <Repeat className="h-3.5 w-3.5" aria-hidden />
            Open LeadLoop
          </Link>
        </Button>
        <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => setCreateOpen(true)}>
          <Plus className="h-3.5 w-3.5" aria-hidden />
          New Manual Lead
        </Button>
        <Button asChild size="sm" variant="outline" className="gap-1.5">
          <Link to="/demo/lcc/submissions">
            <Inbox className="h-3.5 w-3.5" aria-hidden />
            View Form Submissions
          </Link>
        </Button>
      </div>

      {isLoading && <LccLoadingCard />}
      {error != null && <LccErrorCard title="Couldn't load the dashboard" error={error} />}

      {data && (
        <>
          {/* ---------- summary cards (numbers mirror LeadLoop) ---------- */}
          <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <StatTile label="Due now" value={data.cards.dueNow} to="/demo/lcc/loop" />
            <StatTile label="Overdue" value={data.cards.overdue} to="/demo/lcc/loop" urgent />
            <StatTile label="Awaiting approval" value={data.cards.pendingApprovals} to="/demo/lcc/loop" />
            <StatTile label="Unverified leads" value={data.cards.unverifiedLeads} to="/demo/lcc/loop" />
            <StatTile label="Converted this month" value={data.cards.convertedThisMonth} to="/demo/lcc/reports" />
          </section>

          {/* ---------- recent activity ---------- */}
          <section className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h2 className="flex items-center gap-1.5 text-[14px] font-semibold">
                <History className="h-4 w-4 text-primary" aria-hidden />
                Recent activity
              </h2>
              <Link to="/activity" className="text-[12px] text-primary underline-offset-2 hover:underline">
                Full log
              </Link>
            </div>
            <Card className="p-3">
              {data.recentEvents.length === 0 ? (
                <p className="py-4 text-center text-[12.5px] text-muted-foreground">
                  No activity yet.
                </p>
              ) : (
                <ul className="space-y-1.5 py-1">
                  {data.recentEvents.map((event) => (
                    <li key={event.id} className="text-[12px] leading-snug">
                      <Link
                        to={`/demo/lcc/leads/${event.leadId}`}
                        className="font-medium underline-offset-2 hover:underline"
                      >
                        {formatLccLabel(event.eventType)}
                      </Link>{' '}
                      <span className="text-muted-foreground">
                        {event.title} · {formatLccDateTime(event.createdAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </section>
        </>
      )}

      <CreateLeadDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

// ---------- pieces ----------

function StatTile({
  label,
  value,
  to,
  urgent,
}: {
  label: string;
  value: number;
  to: string;
  urgent?: boolean;
}) {
  return (
    <Link to={to}>
      <Card className="flex h-full flex-col gap-1 p-3 transition-colors hover:bg-muted/40">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</span>
        <span
          className={`text-[22px] font-semibold leading-none tabular-nums ${
            urgent && value > 0 ? 'text-destructive' : ''
          }`}
        >
          {value}
          <ArrowRight className="ml-1 inline h-3 w-3 align-baseline text-muted-foreground/50" aria-hidden />
        </span>
      </Card>
    </Link>
  );
}
